import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { FIVE_MINUTES, ONE_MINUTE } from '@/constants/time';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

/**
 * Paginated payload returned by the publications endpoint.
 * Includes pagination metadata plus the publications array.
 */
export type UserPublicationsData = NonNullable<
  paths['/v2/users/{id}/publications']['get']['responses'][200]['content']['application/json']['data']
>;

/**
 * A single publication item. Several fields are nullable because the source
 * data is incomplete: journal, year, doi, and pubmedId may be null, and
 * authors may be an empty array.
 */
export type Publication = UserPublicationsData['publications'][number];

/**
 * Fetches a page of an expert's publications.
 *
 * The endpoint is public and always responds 200 with a possibly empty list,
 * so an empty result is the normal state, not an error. Results arrive
 * newest-first by year (nulls last) from the server; no client sort is needed.
 *
 * @param id - Numeric user id of the expert
 * @param page - 1-based page number (default 1)
 * @param limit - Page size, max 50 (default 50, so "Show all" covers the common case)
 * @returns The paginated publications payload, or undefined on a server fault
 */
export async function getUserPublications(id: number, page = 1, limit = 50) {
  const { data } = await fetchClient.GET('/v2/users/{id}/publications', {
    params: { path: { id }, query: { page, limit } },
  });

  return data?.data;
}

/** Page size for the infinite publications list. */
const PUBLICATIONS_PAGE_SIZE = 20;

/**
 * Infinite-query options for fetching an expert's publications page by page.
 * Stops fetching once all publications are loaded.
 *
 * @param id - Numeric user id of the expert
 */
export function getUserPublicationsInfiniteQueryOptions(id: number) {
  return infiniteQueryOptions({
    queryKey: ['userPublications', 'infinite', id],
    queryFn: ({ pageParam }) => getUserPublications(id, pageParam, PUBLICATIONS_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + (page?.publications?.length ?? 0), 0);

      const total = lastPage?.total ?? 0;

      return loaded < total ? allPages.length + 1 : undefined;
    },
    staleTime: FIVE_MINUTES,
    gcTime: 10 * ONE_MINUTE,
  });
}

/** Endpoint maximum, so the fewest round trips cover a long publication list. */
const ALL_PUBLICATIONS_PAGE_SIZE = 50;

/**
 * Upper bound on pages fetched, 1,000 publications. A runaway `total` must not
 * turn one keystroke into an unbounded request loop.
 */
const MAX_PUBLICATION_PAGES = 20;

/**
 * Fetches every page of an expert's publications, up to the page cap.
 *
 * @param id - Numeric user id of the expert
 * @returns All publications in the server's newest-first order
 */
export async function getAllUserPublications(id: number): Promise<Publication[]> {
  const publications: Publication[] = [];

  for (let index = 1; index <= MAX_PUBLICATION_PAGES; index++) {
    const page = await getUserPublications(id, index, ALL_PUBLICATIONS_PAGE_SIZE);

    const items = page?.publications ?? [];

    publications.push(...items);

    if (items.length === 0 || publications.length >= (page?.total ?? 0)) {
      break;
    }
  }

  return publications;
}

/**
 * Query options for every one of an expert's publications. The `.mypubs` dot
 * phrase filters this list locally as the expert types, so it needs the whole
 * list — a first page alone would hide anything older than its last entry.
 *
 * @param id - Numeric user id of the expert
 */
export function getAllUserPublicationsQueryOptions(id: number) {
  return queryOptions({
    queryKey: ['userPublications', 'all', id],
    queryFn: () => getAllUserPublications(id),
    staleTime: FIVE_MINUTES,
    gcTime: 10 * ONE_MINUTE,
  });
}
